import {
  Checkbox,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  TreeSelect,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  ruleInn,
  ruleNumberLot,
  ruleRequired,
  ruleSummMinMax,
} from "../../../constants/rules/RulesConstantsForms";
import { useAppSelector } from "../../../store/reduxHooks";
import { SELECT_APPLICATIONS_TYPE_BG } from "../../../store/selectors/applicatoinsSelector";
import { RootState } from "../../../store/Store";
import { UI_ITODatePicker } from "../../../UI_ITO/UI_ITODatePicker/UI_ITODatePicker";
import { datePickerGetValuePropsFormItem } from "../../../utils/utilities/DatePickerGetValuePropsFormItem";
import { formatter, parser } from "../../../utils/utilities/InputSumFunctions";
import { validateDate } from "../../../utils/utilities/ValidateDate";
import { InputNumberLot } from "../UI/InputNumberLot";

import classes from "./PurchaseContainer.module.css";

dayjs.locale("ru");

type LotType = {
  title: string;
  value: string;
};

type PurchaseCustomer = {
  value: string;
  title: string;
  children: LotType[];
  selectable: boolean;
  labelInValue: boolean;
};

interface IPurchaseContainerProps {
  handleChangeLotValue: () => void;

  className?: string;
}

/**
 * Компонент контейнера при создании заявки.
 * В зависимости от условий бг отображаются нужные поля.
 *
 * @param {IPurchaseContainerProps} props - Пропсы компонента.
 * @param {() => void} props.handleChangeLotValue - Функция для изменения значения лота.
 * @param {string} [props.className] - Дополнительный класс для секций.
 */
export function PurchaseContainer(
  props: IPurchaseContainerProps,
): ReactElement {
  const { handleChangeLotValue, className = "" } = props;
  const sectionClasses = `${classes.form__fieldset} ${className}`;
  const typeBgList = useAppSelector(SELECT_APPLICATIONS_TYPE_BG);
  const [isHandInput, setIsHandInput] = useState<boolean>(false);

  const SelectTypeBg = typeBgList
    .filter((item) => item.id !== 5)
    .map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  const applicationCreateSlice = useSelector(
    (state: RootState) => state.applicationCreateSlice,
  );

  const plainOptions = [
    { label: "223-ФЗ", value: "223" },
    { label: "44-ФЗ", value: "44" },
    { label: "185-ФЗ (615-ПП)", value: "185" },
    { label: "Коммерческая", value: "commerce" },
  ];

  const [selectorData, setSelectorData] = useState<PurchaseCustomer[]>([]);

  // const currentDate = new Date().toISOString().split("T")[0];
  // Собирает данные по кастомерам в массив для селектора
  useEffect(() => {
    const dataArray: PurchaseCustomer[] = [];
    applicationCreateSlice.PURCHASE_REQUEST.data?.purchase_lot?.forEach(
      (customer) => {
        const lotArray: LotType[] = [];
        // Собираем лоты в children каждому покупателю
        if (customer.lots) {
          customer.lots?.forEach((lot) =>
            lotArray.push({
              value: `${customer.inn_customer} //: ${lot.name_lot}`,
              title: lot.name_lot,
            }),
          );
        }
        if (customer.lot) {
          lotArray.push({
            value: `${customer.inn_customer} //: ${customer.lot.name_lot}`,
            title: customer.lot.name_lot,
          });
        }

        dataArray.push({
          title: `ИНН: ${customer.inn_customer}`,
          value: customer.inn_customer,
          children: lotArray,
          selectable: false,
          labelInValue: true,
        });
      },
    );

    setSelectorData(dataArray);
  }, [applicationCreateSlice.PURCHASE_REQUEST]);

  const STAGE_PURCHASE_KEY = applicationCreateSlice.MATCH_STATE;

  const CONST_JSX_COMPONENTS_ELEMENT = {
    STAGE_IKZ: {
      IKZ: (
        <Form.Item
          name="ikz"
          label="ИКЗ"
          required={false}
          className={classes.form__field}
          is-full-width="true"
          column-count="2"
        >
          <Input size="large" />
        </Form.Item>
      ),
      IKZ_REQUIRED: (
        <Form.Item
          name="ikz"
          label="ИКЗ"
          required={true}
          rules={[ruleRequired]}
          className={classes.form__field}
          is-full-width="true"
          column-count="2"
        >
          <Input size="large" />
        </Form.Item>
      ),
      NOT_DATA: null,
    },
    PROVISION: (
      <div className={`${classes.form__fieldWrapper} `}>
        <Form.Item
          noStyle={true}
          valuePropName="checked"
          name={"is_reprovision"}
        >
          <Checkbox>Переобеспечение</Checkbox>
        </Form.Item>
        <Form.Item noStyle={true} valuePropName="checked" name={"smp"}>
          <Checkbox>СМП</Checkbox>
        </Form.Item>
      </div>
    ),
    BANK_GUARANTEE: (
      <div className={classes.form__fieldWrapper}>
        <Form.Item
          className={classes.form__field}
          label="Ссылка на закупку (или план график)"
          name={"link"}
          required={true}
          rules={[ruleRequired]}
        >
          <Input size="large" />
        </Form.Item>
        <Form.Item
          label="Предмет контракта"
          className={classes.form__field}
          name={"subject_contract"}
          rules={[ruleRequired]}
          required={true}
        >
          <TextArea rows={5} size="large" />
        </Form.Item>
      </div>
    ),
    CONTRACT_STAGE_TITLE: (
      <div className={classes.form__legend}>Цена контракта</div>
    ),
    AUCTION_STAGE_TITLE: <div className={classes.form__legend}>Аукцион</div>,
    STAGE_START_BG_TITLE: (
      <div className={classes.form__legend}>Начало и окончание БГ</div>
    ),
  };
  const RENDER_JSX_MATCH = {
    STAGE_SEARCH_BG: {
      STAGE_SEARCH_1: (
        <>
          <Form.Item
            className={classes.form__field}
            name="purchase_number"
            label="Номер закупки"
            rules={[ruleRequired]}
            required={true}
            is-full-width="true"
            column-count="2"
          >
            <Input size="large" />
          </Form.Item>

          <div className={classes.inputModifiableContainer}>
            <Form.Item noStyle={true} name="is_hand_input">
              <Checkbox
                className={classes.inputModifier}
                defaultChecked={false}
                onChange={(e) => setIsHandInput(e.target.checked)}
              >
                Ввести вручную
              </Checkbox>
            </Form.Item>
            {isHandInput ? (
              <Form.Item
                name="name_lot"
                label="Наименование лота"
                className={classes.inputModifiable}
                rules={[ruleRequired]}
                required={true}
              >
                <Input placeholder="Введите наименование лота" type="text" />
              </Form.Item>
            ) : (
              <Form.Item
                name="name_lot"
                label="Заказчик и лот"
                className={classes.inputModifiable}
                rules={[ruleRequired]}
                required={true}
              >
                <TreeSelect
                  listHeight={350}
                  size="large"
                  showSearch
                  placeholder="Выберите заказчика и лот"
                  treeData={selectorData}
                  onSelect={handleChangeLotValue}
                />
              </Form.Item>
            )}
          </div>
          {CONST_JSX_COMPONENTS_ELEMENT.STAGE_IKZ[STAGE_PURCHASE_KEY.STAGE_IKZ]}
        </>
      ),
      NOT_DATA: null,
    },
    STAGE_CHECK_BOX_RE_PROVISION: {
      PROVISION_STAGE_ALL: (
        <>
          <div
            className={classes.form__fieldset}
            is-full-width="true"
            column-count="4"
          >
            {CONST_JSX_COMPONENTS_ELEMENT.PROVISION}
            <div className={classes.form__fieldWrapper}>
              <Form.Item
                valuePropName="checked"
                noStyle={true}
                name={"is_closed"}
              >
                <Checkbox>Закрытый аукцион</Checkbox>
              </Form.Item>
              <Form.Item
                noStyle={true}
                valuePropName="checked"
                name={"is_sole_supplier"}
              >
                <Checkbox>Единственный поставщик</Checkbox>
              </Form.Item>
            </div>
          </div>
          {CONST_JSX_COMPONENTS_ELEMENT.BANK_GUARANTEE}
        </>
      ),
      PROVISION_STAGE_PROVISION: (
        <>
          <div
            className={classes.form__fieldset}
            is-full-width="true"
            column-count="4"
          >
            {CONST_JSX_COMPONENTS_ELEMENT.PROVISION}
          </div>

          {CONST_JSX_COMPONENTS_ELEMENT.BANK_GUARANTEE}
        </>
      ),
      NOT_DATA: null,
    },

    STATE_CUSTOMER: {
      CUSTOMER_STAGE_ALL: (
        <div className={classes.form__fieldWrapper}>
          <Form.Item
            className={classes.form__field}
            label="ИНН Заказчика"
            name="inn_customer"
            required={true}
            rules={[ruleRequired, ruleInn]}
          >
            <Input placeholder="ИНН Заказчика" size="large" />
          </Form.Item>
          <Form.Item
            required={true}
            label="Наименование организации"
            className={classes.form__field}
            name={"name_customer"}
            rules={[ruleRequired]}
          >
            <TextArea rows={5} size="large" />
          </Form.Item>
        </div>
      ),
      NOT_DATA: null,
    },
    STAGE_CONTRACT: {
      CONTRACT_STAGE_1: (
        <div className={sectionClasses} role="group" aria-describedby="">
          {CONST_JSX_COMPONENTS_ELEMENT.CONTRACT_STAGE_TITLE}
          <Form.Item
            name="initial_price"
            label="Начальная цена контракта"
            required={true}
            className={classes.form__field}
            rules={[ruleSummMinMax, ruleRequired]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="total_sum"
            required={true}
            label="Итоговая цена контракта"
            className={classes.form__field}
            rules={[ruleSummMinMax, ruleRequired]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="sum_avance"
            label="Сумма аванса по контракту"
            className={classes.form__field}
            rules={[ruleSummMinMax]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            valuePropName="checked"
            noStyle={true}
            name={"is_indisputable"}
          >
            <Checkbox is-full-width="true">
              Есть требование о бесспорном списании средств
            </Checkbox>
          </Form.Item>
        </div>
      ),
      CONTRACT_STAGE_2: null,
      CONTRACT_STAGE_3: (
        <div className={sectionClasses} role="group" aria-describedby="">
          {CONST_JSX_COMPONENTS_ELEMENT.CONTRACT_STAGE_TITLE}
          <Form.Item
            name="initial_price"
            label="Начальная цена контракта"
            required={true}
            className={classes.form__field}
            rules={[ruleRequired, ruleSummMinMax]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="sum_bg_execution"
            label="Сумма обеспечения на исполнение"
            required={true}
            className={classes.form__field}
            rules={[ruleSummMinMax, ruleRequired]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="sum_avance"
            label="Сумма аванса по контракту"
            className={classes.form__field}
            rules={[ruleSummMinMax]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            valuePropName="checked"
            noStyle={true}
            name={"is_indisputable"}
          >
            <Checkbox is-full-width="true">
              Есть требование о бесспорном списании средств
            </Checkbox>
          </Form.Item>
        </div>
      ),
      CONTRACT_STAGE_4: (
        <div className={sectionClasses} role="group" aria-describedby="">
          {CONST_JSX_COMPONENTS_ELEMENT.CONTRACT_STAGE_TITLE}
          <Form.Item
            name="initial_price"
            label="Начальная цена контракта"
            rules={[ruleSummMinMax, ruleRequired]}
            required={true}
            className={classes.form__field}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="total_sum"
            required={true}
            label="Итоговая цена контракта"
            rules={[ruleSummMinMax, ruleRequired]}
            className={classes.form__field}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="sum_avance"
            label="Сумма аванса по контракту"
            rules={[ruleSummMinMax, ruleRequired]}
            className={classes.form__field}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            valuePropName="checked"
            noStyle={true}
            name={"is_indisputable"}
          >
            <Checkbox is-full-width="true">
              Есть требование о бесспорном списании средств
            </Checkbox>
          </Form.Item>
        </div>
      ),
      NOT_DATA: <></>,
    },
    STAGE_AUCTION: {
      AUCTION_STAGE_1: (
        <div className={sectionClasses} role="group" aria-describedby="">
          {CONST_JSX_COMPONENTS_ELEMENT.AUCTION_STAGE_TITLE}
          <Form.Item
            label="Дата проведения аукциона "
            name={"purchase_total_at"}
            required={true}
            getValueProps={datePickerGetValuePropsFormItem}
            rules={[ruleRequired]}
            className={classes.form__field}
          >
            <UI_ITODatePicker className={classes.form__field} size="large" />
          </Form.Item>
          <Form.Item
            name="sum_bg_execution"
            label="Сумма обеспечения"
            rules={[ruleRequired, ruleSummMinMax]}
            required={true}
            className={classes.form__field}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="number_lot"
            label="Номер лота"
            required={true}
            rules={[ruleRequired, ruleNumberLot]}
            className={classes.form__field}
          >
            <InputNumberLot size="large" />
          </Form.Item>
        </div>
      ),
      AUCTION_STAGE_2: (
        <div className={sectionClasses} role="group" aria-describedby="">
          {CONST_JSX_COMPONENTS_ELEMENT.AUCTION_STAGE_TITLE}
          <Form.Item
            name="sum_bg_obligation"
            label="Сумма обеспечения"
            className={classes.form__field}
            required={true}
            rules={[ruleRequired, ruleSummMinMax]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="number_lot"
            label="Номер лота"
            required={true}
            className={classes.form__field}
            rules={[ruleRequired, ruleNumberLot]}
          >
            <InputNumberLot size="large" />
          </Form.Item>
        </div>
      ),
      AUCTION_STAGE_3: (
        <div className={sectionClasses} role="group" aria-describedby="">
          {CONST_JSX_COMPONENTS_ELEMENT.AUCTION_STAGE_TITLE}
          <Form.Item
            label="Окончание подачи заявок"
            name={"purchase_end_at"}
            className={classes.form__field}
            getValueProps={datePickerGetValuePropsFormItem}
            rules={[ruleRequired]}
          >
            <UI_ITODatePicker className={classes.form__field} size="large" />
          </Form.Item>
          <Form.Item
            name="sum_bg"
            label="Сумма обеспечения"
            className={classes.form__field}
            required={true}
            rules={[ruleRequired, ruleSummMinMax]}
          >
            <InputNumber
              controls={false}
              className={classes.form__field}
              formatter={formatter}
              parser={parser}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="number_lot"
            label="Номер лота"
            required={true}
            className={classes.form__field}
            rules={[ruleRequired, ruleNumberLot]}
          >
            <InputNumberLot size="large" />
          </Form.Item>
        </div>
      ),
      AUCTION_STAGE_4: <></>,
      NOT_DATA: null,
    },
    STAGE_START_BG: {
      START_BG_STAGE_1: (
        <div className={sectionClasses} role="group" aria-describedby="">
          {CONST_JSX_COMPONENTS_ELEMENT.STAGE_START_BG_TITLE}
          <Form.Item
            label="Дата начала бг"
            required={true}
            name={"start_bg_at"}
            className={classes.form__field}
            getValueProps={datePickerGetValuePropsFormItem}
            rules={[
              ruleRequired,
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const fieldValue = getFieldValue("start_bg_at");
                  const inputValue = new Date(value);

                  return validateDate({ fieldValue, inputValue });
                },
              }),
            ]}
            initialValue={dayjs(new Date())}
          >
            <UI_ITODatePicker className={classes.form__field} size="large" />
          </Form.Item>
          <Form.Item
            label="Дата окончания бг"
            required={true}
            name={"finish_bg_at"}
            className={classes.form__field}
            getValueProps={datePickerGetValuePropsFormItem}
            rules={[
              ruleRequired,
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const fieldValue = getFieldValue("finish_bg_at");
                  const inputValue = new Date(value);

                  return validateDate({ fieldValue, inputValue });
                },
              }),
            ]}
          >
            <UI_ITODatePicker className={classes.form__field} size="large" />
          </Form.Item>
        </div>
      ),
      START_BG_STAGE_2: null,
      START_BG_STAGE_3: null,
      NOT_DATA: null,
    },
  };

  return (
    <>
      <div className={sectionClasses} role="group" aria-describedby="">
        <div className={classes.form__legend}>Тип гарантии и закупка</div>
        <div
          className={classes.form__field}
          is-full-width="true"
          role="group"
          aria-describedby="purchase_legend"
        >
          <div className={classes.form__radioLegend} id="purchase_legend">
            Закупка
          </div>
          <Form.Item
            name="fz"
            required={true}
            rules={[ruleRequired]}
            noStyle={true}
          >
            <Radio.Group
              className={classes.radioGroup}
              options={plainOptions}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
        </div>
        <Form.Item
          className={classes.form__field}
          name="bg_type_id"
          label="Тип банковской гарантии"
          rules={[ruleRequired]}
          required={true}
          is-full-width="true"
          column-count="2"
        >
          <Select
            popupClassName={classes.selectDropDown}
            options={SelectTypeBg}
            size="large"
          />
        </Form.Item>
        {RENDER_JSX_MATCH.STAGE_SEARCH_BG[STAGE_PURCHASE_KEY.STAGE_SEARCH_BG]}

        {
          RENDER_JSX_MATCH.STAGE_CHECK_BOX_RE_PROVISION[
            STAGE_PURCHASE_KEY.STAGE_CHECK_BOX_RE_PROVISION
          ]
        }
        {RENDER_JSX_MATCH.STATE_CUSTOMER[STAGE_PURCHASE_KEY.STATE_CUSTOMER]}
      </div>
      {RENDER_JSX_MATCH.STAGE_CONTRACT[STAGE_PURCHASE_KEY.STAGE_CONTRACT]}
      {RENDER_JSX_MATCH.STAGE_AUCTION[STAGE_PURCHASE_KEY.STAGE_AUCTION]}
      {RENDER_JSX_MATCH.STAGE_START_BG[STAGE_PURCHASE_KEY.STAGE_START_BG]}
    </>
  );
}
